<template>
  <!-- Section header -->
  <div class="sticky top-[53px] z-[8] flex items-center gap-2 px-4 py-2 bg-[var(--color-bg)] border-b border-[var(--color-border)]">
    <!-- Rename inline -->
    <template v-if="renaming">
      <BaseInput
        ref="renameInput"
        v-model="renameVal"
        class="flex-1 px-2 py-1 bg-[var(--color-surface)] border border-blue-500 rounded text-[var(--color-text)] text-sm font-semibold outline-none"
        maxlength="100"
        @keydown.enter.prevent="commitRename"
        @keydown.escape="renaming = false"
        @blur="commitRename"
      />
    </template>
    <template v-else>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0 text-[var(--color-accent)]">
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
      </svg>
      <span
        class="flex-1 font-semibold text-sm text-[var(--color-text)] truncate cursor-pointer hover:text-[var(--color-accent)] transition-colors"
        @click="startRename"
        :title="$t('projectItems.renameSection')"
      >{{ section.name }}</span>
      <span v-if="itemCount > 0" class="badge badge-muted badge-pill shrink-0">{{ itemCount }}</span>
    </template>

    <!-- Actions -->
    <button
      v-if="!renaming"
      class="btn-icon shrink-0"
      :title="$t('projectItems.renameSection')"
      @click="startRename"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    </button>
    <button
      v-if="!renaming"
      class="btn-icon danger shrink-0"
      :title="$t('projectItems.deleteSection')"
      @click="$emit('delete', section)"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14H6L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4h6v2"/>
      </svg>
    </button>
    <button
      v-if="renaming"
      class="btn primary small shrink-0"
      :disabled="!renameVal.trim()"
      @click="commitRename"
    >{{ $t('common.save') }}</button>
    <button
      v-if="renaming"
      class="btn ghost small shrink-0"
      @click="renaming = false"
    >{{ $t('common.cancel') }}</button>
  </div>

  <!-- Items -->
  <div class="list-stack">
    <slot />
  </div>

  <!-- Add items to this section -->
  <div class="px-4 pt-1 pb-3">
    <button class="btn ghost small" @click="$emit('browse', section)">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
      {{ $t('projectItems.addItemsTo', { name: section.name }) }}
    </button>
  </div>
</template>

<script setup>
import { ref, nextTick } from 'vue'
import BaseInput from '@/components/base/BaseInput.vue'

const props = defineProps({
  section:   { type: Object, required: true },
  itemCount: { type: Number, default: 0 },
})

const emit = defineEmits(['rename', 'delete', 'browse'])

const renaming   = ref(false)
const renameVal  = ref('')
const renameInput = ref(null)

function startRename() {
  renameVal.value = props.section.name
  renaming.value  = true
  nextTick(() => renameInput.value?.focus())
}

async function commitRename() {
  const name = renameVal.value.trim()
  renaming.value = false
  if (!name || name === props.section.name) return
  emit('rename', { section: props.section, name })
}
</script>
